import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { heroBannersApi } from '../services/api';
import { trackBannerView, trackBannerClick } from '../services/bannerTracker';

/**
 * HeroCarousel — banner rotativo exibido no topo da Home.
 *
 * Props:
 *  - municipioId?: string   — Se preenchido, tenta banners municipais primeiro.
 *  - municipioSlug?: string
 *  - fallback: ReactNode    — Renderizado quando não há banners cadastrados.
 *  - onCtaClick?: (banner, cta) => boolean  — Retornar true intercepta a navegação padrão.
 *  - intervalMs: número     — Autoplay (default 6000).
 */
export default function HeroCarousel({
  municipioId,
  municipioSlug,
  fallback,
  onCtaClick,
  intervalMs = 6000,
}) {
  const [banners, setBanners] = useState(null); // null = ainda carregando; [] = vazio
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = {};
        if (municipioId) params.municipio_id = municipioId;
        else if (municipioSlug) params.municipio_slug = municipioSlug;
        const data = await heroBannersApi.publicList(params);
        if (!alive) return;
        setBanners(Array.isArray(data) ? data : []);
        setCurrent(0);
      } catch (_e) {
        if (!alive) return;
        setBanners([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [municipioId, municipioSlug]);

  const goTo = useCallback(
    (idx) => {
      if (!banners || banners.length === 0) return;
      const total = banners.length;
      setCurrent(((idx % total) + total) % total);
    },
    [banners]
  );

  const next = useCallback(() => goTo(current + 1), [goTo, current]);
  const prev = useCallback(() => goTo(current - 1), [goTo, current]);

  // Autoplay
  useEffect(() => {
    if (paused || !banners || banners.length <= 1) return;
    timerRef.current = setTimeout(() => {
      goTo(current + 1);
    }, intervalMs);
    return () => clearTimeout(timerRef.current);
  }, [current, paused, banners, goTo, intervalMs]);

  // Track view on slide change (dedup por sessão+dia é feito no tracker)
  const currentBannerId = banners && banners[current] ? banners[current].id : null;
  const currentBannerMun = banners && banners[current] ? banners[current].municipio_id : null;
  useEffect(() => {
    if (currentBannerId) {
      trackBannerView('hero', currentBannerId, { municipioId: currentBannerMun || municipioId });
    }
  }, [currentBannerId, currentBannerMun, municipioId]);

  // Loading
  if (banners === null) {
    return (
      <div
        className="relative bg-gradient-to-r from-[#56007F] to-[#3f005f] w-full"
        style={{ minHeight: 380 }}
        data-testid="hero-carousel-loading"
      />
    );
  }

  // Empty → fallback
  if (banners.length === 0) return fallback || null;

  const b = banners[current];

  const handleCta = (kind) => {
    const cta =
      kind === 'primary'
        ? { texto: b.cta_primario_texto, link: b.cta_primario_link, kind }
        : { texto: b.cta_secundario_texto, link: b.cta_secundario_link, kind };
    // Track click sempre (mesmo se onCtaClick interceptar navegação)
    trackBannerClick('hero', b.id, { municipioId: b.municipio_id || municipioId });
    if (typeof onCtaClick === 'function') {
      const intercepted = onCtaClick(b, cta);
      if (intercepted) return;
    }
    if (cta.link) {
      if (cta.link.startsWith('http')) {
        window.open(cta.link, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = cta.link;
      }
    }
  };

  return (
    <div
      className="relative bg-cover bg-center bg-no-repeat overflow-hidden"
      style={{
        backgroundImage: b.imagem_url ? `url(${b.imagem_url})` : undefined,
        backgroundColor: b.bg_color || '#56007F',
      }}
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Overlay para leitura */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#56007F]/90 via-[#56007F]/70 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="max-w-xl">
          <h1
            className="text-3xl md:text-5xl font-extrabold mb-4 text-white text-left whitespace-pre-line"
            style={{ fontFamily: 'Manrope' }}
            data-testid="hero-carousel-titulo"
            key={`t-${current}`}
          >
            {b.titulo}
          </h1>
          {b.subtitulo ? (
            <p
              className="text-white/90 text-lg md:text-xl mb-8 text-left whitespace-pre-line"
              data-testid="hero-carousel-subtitulo"
              key={`s-${current}`}
            >
              {b.subtitulo}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-3 items-center">
            {b.cta_primario_texto ? (
              <button
                onClick={() => handleCta('primary')}
                className="bg-[#FFA52A] hover:bg-[#e89420] text-[#56007F] font-bold rounded-full px-8 py-4 text-lg transition-all shadow-lg hover:shadow-xl active:scale-95"
                data-testid="hero-carousel-cta-primary"
              >
                {b.cta_primario_texto}
              </button>
            ) : null}
            {b.cta_secundario_texto ? (
              <button
                onClick={() => handleCta('secondary')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/60 font-semibold rounded-full px-6 py-3 text-base backdrop-blur-sm transition-all"
                data-testid="hero-carousel-cta-secondary"
              >
                {b.cta_secundario_texto}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Controls */}
      {banners.length > 1 ? (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors backdrop-blur-sm"
            aria-label="Banner anterior"
            data-testid="hero-carousel-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors backdrop-blur-sm"
            aria-label="Próximo banner"
            data-testid="hero-carousel-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2"
            data-testid="hero-carousel-dots"
          >
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Ir para banner ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === current ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
                }`}
                data-testid={`hero-carousel-dot-${i}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
